import type {
  ConnectorImportMode,
  ImportJobProgress,
} from '../services/api';

export interface IngestionHistoryItem {
  id: string;
  type: 'upload' | 'import';
  index_id: string;
  created_at: string;
  updated_at: string;
  status: string;
  file_name?: string;
  file_size_bytes?: number;
  media_type?: string;
  duration_seconds?: number | null;
  thumbnail_data?: string | null;
  video_id?: string;
  error_message?: string | null;
  connector_id?: string;
  connector_name?: string;
  source_prefix?: string;
  file_pattern?: string;
  recursive?: boolean;
  import_mode_override?: ConnectorImportMode | null;
  job_id?: string;
  progress?: ImportJobProgress;
}

interface ImportHistoryProjectionOptions {
  indexId?: string | null;
  connectorName?: string;
  observedAt?: string;
  statusFallback?: string;
}

const TERMINAL_INGESTION_STATUSES = new Set([
  'cancelled',
  'canceled',
  'completed',
  'completed_with_failures',
  'failed',
  'partial_completed',
  'success',
  'succeeded',
]);

const hasOwn = (value: Record<string, unknown>, key: string): boolean => (
  Object.prototype.hasOwnProperty.call(value, key)
);

const asNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return normalized || null;
};

const asTimestamp = (value: unknown): string | null => {
  const normalized = asNonEmptyString(value);
  if (!normalized) return null;
  return Number.isFinite(new Date(normalized).getTime()) ? normalized : null;
};

const timestampMs = (value: string | null | undefined): number => {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

export const isTerminalIngestionStatus = (status: unknown): boolean => (
  TERMINAL_INGESTION_STATUSES.has(String(status || '').trim().toLowerCase())
);

export const getImportHistoryStatusFallback = (eventType: string): string | undefined => {
  switch (String(eventType || '').trim().toLowerCase()) {
    case 'import_job.created':
      return 'pending';
    case 'import_job.scanning':
      return 'scanning';
    case 'import_job.started':
    case 'import_job.progress':
    case 'import_job.video_imported':
      return 'importing';
    case 'import_job.completed':
      return 'completed';
    case 'import_job.partial_completed':
      return 'partial_completed';
    case 'import_job.failed':
      return 'failed';
    case 'import_job.cancelled':
      return 'cancelled';
    default:
      return undefined;
  }
};

/**
 * Project both REST import-job snapshots and partial realtime payloads through
 * one shape. Optional fields are emitted only when the source actually owns
 * them, so a sparse stream event cannot erase richer history already loaded.
 */
export const projectImportIngestionHistoryItem = (
  sourceValue: Record<string, unknown>,
  options: ImportHistoryProjectionOptions = {},
): IngestionHistoryItem | null => {
  const jobId = asNonEmptyString(sourceValue.job_id);
  const indexId = (
    asNonEmptyString(options.indexId)
    || asNonEmptyString(sourceValue.target_index_id)
    || asNonEmptyString(sourceValue.index_id)
  );
  if (!jobId || !indexId) return null;

  const observedAt = asTimestamp(options.observedAt);
  const fallbackTimestamp = observedAt || new Date().toISOString();
  const createdAt = asTimestamp(sourceValue.created_at) || fallbackTimestamp;
  const updatedAt = (
    asTimestamp(sourceValue.updated_at)
    || asTimestamp(sourceValue.completed_at)
    // A stream event's envelope timestamp orders successive progress events.
    // Import payloads otherwise carry the job's original started_at value,
    // which cannot distinguish a fresh event from an older REST snapshot.
    || observedAt
    || asTimestamp(sourceValue.started_at)
    || asTimestamp(sourceValue.created_at)
    || fallbackTimestamp
  );
  const status = (
    asNonEmptyString(sourceValue.status)
    || asNonEmptyString(options.statusFallback)
    || 'pending'
  );

  const projected: IngestionHistoryItem = {
    id: `import-${jobId}`,
    type: 'import',
    index_id: indexId,
    created_at: createdAt,
    updated_at: updatedAt,
    status,
    job_id: jobId,
  };

  if (hasOwn(sourceValue, 'connector_id')) {
    projected.connector_id = String(sourceValue.connector_id || '');
  }
  if (options.connectorName) {
    projected.connector_name = options.connectorName;
  }
  if (hasOwn(sourceValue, 'source_prefix')) {
    projected.source_prefix = String(sourceValue.source_prefix || '');
  }
  if (hasOwn(sourceValue, 'file_pattern')) {
    projected.file_pattern = String(sourceValue.file_pattern || '*');
  }
  if (hasOwn(sourceValue, 'recursive') && typeof sourceValue.recursive === 'boolean') {
    projected.recursive = sourceValue.recursive;
  }
  if (hasOwn(sourceValue, 'import_mode_override')) {
    projected.import_mode_override = typeof sourceValue.import_mode_override === 'string'
      ? sourceValue.import_mode_override as ConnectorImportMode
      : null;
  }
  if (
    hasOwn(sourceValue, 'progress')
    && sourceValue.progress
    && typeof sourceValue.progress === 'object'
    && !Array.isArray(sourceValue.progress)
  ) {
    projected.progress = sourceValue.progress as ImportJobProgress;
  }
  if (hasOwn(sourceValue, 'error_message')) {
    projected.error_message = typeof sourceValue.error_message === 'string'
      ? sourceValue.error_message
      : null;
  }

  return projected;
};

const mergeHistoryItem = (
  current: IngestionHistoryItem,
  incoming: IngestionHistoryItem,
): IngestionHistoryItem => {
  const currentTerminal = isTerminalIngestionStatus(current.status);
  const incomingTerminal = isTerminalIngestionStatus(incoming.status);

  // Import jobs do not leave terminal state. Replayed or delayed reads may
  // enrich a terminal entry, but cannot move it back into an active state.
  if (currentTerminal && !incomingTerminal) {
    return { ...incoming, ...current };
  }
  if (incomingTerminal && !currentTerminal) {
    return { ...current, ...incoming };
  }

  const currentTime = timestampMs(current.updated_at || current.created_at);
  const incomingTime = timestampMs(incoming.updated_at || incoming.created_at);
  if (incomingTime < currentTime) {
    // Retain authoritative current values while accepting fields that were
    // absent from the newer sparse projection.
    return { ...incoming, ...current };
  }
  return { ...current, ...incoming };
};

export const mergeIngestionHistoryEntries = (
  currentEntries: IngestionHistoryItem[],
  incomingEntries: IngestionHistoryItem[],
  limit: number = 120,
): IngestionHistoryItem[] => {
  if (!incomingEntries.length) return currentEntries;

  const byId = new Map(currentEntries.map((item) => [item.id, item]));
  incomingEntries.forEach((entry) => {
    const current = byId.get(entry.id);
    byId.set(entry.id, current ? mergeHistoryItem(current, entry) : entry);
  });

  return Array.from(byId.values())
    .sort((a, b) => (
      timestampMs(b.updated_at || b.created_at) - timestampMs(a.updated_at || a.created_at)
    ))
    .slice(0, Math.max(0, limit));
};
